import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import { EmploymentStatus, Purpose } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import { addDebtTypes } from '../../../../shared/mutators/addDebtTypes';
import {
  reloadFrontendToggles,
  saveFrontendToggles,
} from '../../../../shared/mutators/frontendToggles';
import { removeCoAppData } from '../../../../shared/mutators/removeCoAppData';
import { transformIncome } from '../../../../shared/mutators/transformIncomes';
import {
  mapNumberInputsToString,
  mapRadioInputsToBoolean,
  mapRadioInputsToString,
  mapStringInputsToNumber,
} from '../../../../utils';
import type { FinlandFormData } from '../../FormData.fi.types';

const frontendToggles = [
  'Frontend/IsFinnish',
  'Frontend/AddCoApplicant',
  'Frontend/HasHousingDebt',
  'Frontend/HasCreditCardDebt',
  'Frontend/HasPersonalDebt',
  'Frontend/HasStudentDebt',
  'Frontend/HasCarDebt',
] as const;
const booleanRadioInputs = [
  'LoanApplication/MilitaryServiceCompleted',
  'LoanApplication/PoliticallyInfluential',
] as const;
const numericStringInputs = [
  'LoanApplication/NetIncomeMonthly',
  'LoanApplication/ExtraIncomeMonthly',
  'LoanApplication/RentCostMonthly',
  'LoanApplication/OwnerAssociationCostMonthly',
  'LoanApplication/LivingCostMonthly',
  'LoanApplication/PartnerLivingCostMonthly',
  'LoanApplication/CoApplicantMonthlyExpenses',
  'Debt/Student/OutstandingAmount',
  'Debt/Student/MonthlyDebtPayment',
  'Debt/Housing/OutstandingAmount',
  'Debt/Housing/MonthlyDebtPayment',
  'Debt/CreditCard/OutstandingAmount',
  'Debt/CreditCard/MonthlyDebtPayment',
  'Debt/Personal/OutstandingAmount',
  'Debt/Personal/MonthlyDebtPayment',
  'Debt/Personal/RefinanceAmount',
  'Debt/Car/OutstandingAmount',
  'Debt/Car/MonthlyDebtPayment',
  'CoDebt/Housing/OutstandingAmount',
  'CoDebt/Car/OutstandingAmount',
  'CoDebt/CreditCard/OutstandingAmount',
  'CoDebt/Student/OutstandingAmount',
  'CoDebt/Personal/OutstandingAmount',
] as const;

const staticValues = {
  'LoanApplication/MarketCountry': MarketCountryCode.Finland,

  'Person/MarketCountry': MarketCountryCode.Finland,
  'CoPerson/MarketCountry': MarketCountryCode.Finland,

  'Customer/MarketCountry': MarketCountryCode.Finland,
  'Customer/MobilePhoneCountryCode': 358,

  'CoCustomer/MarketCountry': MarketCountryCode.Finland,
  'CoCustomer/MobilePhoneCountryCode': 358,
};

// Map data from Backend Values to Frontend Values
const fromApi = (data: Record<string, unknown>): FinlandFormData => ({
  ...data,
  // Shared
  ...mapNumberInputsToString(data, numericStringInputs),
  ...mapRadioInputsToString(data, booleanRadioInputs),
  ...reloadFrontendToggles(data),
  ...transformIncome(data, 'fromApi'),
  // Market specific
});

// TODO: remove when we are using the new consent service
// note, consent is a part of customer, but is handled in a separate handler
const addConsentFields = (data: FinlandFormData) => {
  const result = {};

  if (data['Ignore/MarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/MarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('fi'),
        Partners: getMarketingConsentPartners('fi'),
        Services: getMarketingConsentServices('fi'),
      },
    });
  }

  if (data['Ignore/CoApplicantMarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/CoApplicantMarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('fi'),
        Partners: getMarketingConsentPartners('fi'),
        Services: getMarketingConsentServices('fi'),
      },
    });
  }

  return result;
};

// Map data from Frontend Values to Backend Values
const toApi = (data: FinlandFormData): FinlandFormData => ({
  ...data,
  // Shared
  ...addDebtTypes(data),
  ...mapRadioInputsToBoolean(data, booleanRadioInputs),
  ...mapStringInputsToNumber(data, numericStringInputs),
  ...saveFrontendToggles(data, frontendToggles),
  ...transformIncome(data, 'toApi'),
  ...removeCoAppData(data),
  // Market specific
  ...staticValues,
  ...addConsentFields(data),
  ...addCitizenshipValues(data),
  ...addJobPosition(data),
});

const addCitizenshipValues = (data: FinlandFormData) => {
  const result = {};

  const isFinnish = data['Frontend/IsFinnish'] === 'Yes';

  Object.assign(result, {
    'LoanApplication/Citizenship': isFinnish
      ? MarketCountryCode.Finland
      : data['LoanApplication/Citizenship'],
  });

  return result;
};

const addJobPosition = (data: FinlandFormData) => {
  const result = {};

  const employmentStatus = data['LoanApplication/EmploymentStatus'];

  if (!employmentStatus) return result;

  if (
    [
      EmploymentStatus.Entrepreneur,
      EmploymentStatus.Farmer,
      EmploymentStatus.Student,
      EmploymentStatus.Pensioner,
    ].includes(employmentStatus)
  ) {
    Object.assign(result, {
      'LoanApplication/JobPosition': employmentStatus,
    });
  }

  return result;
};

const toApiLastSync = (data: FinlandFormData) => {
  const result = {};

  if (
    data['Debt/Personal/RefinanceAmount'] &&
    Number(data['Debt/Personal/RefinanceAmount']) > 0
  ) {
    Object.assign(result, {
      'LoanApplication/Purpose': Purpose.RefinancingLoan,
    });
  }

  return result;
};

export const mutators = {
  fromApi,
  toApi,
  toApiLastSync,
};
